import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Users, ArrowLeft } from 'lucide-react';

const CollaboratorLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      if (userData.role !== 'collaborator') {
        toast.error('This account is not a supervisor / collaborator account. Use the regular login.');
        setLoading(false);
        return;
      }
      toast.success(`Welcome, ${userData.full_name || 'Supervisor'}`);
      navigate('/collaborator-dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-rose-900 via-red-800 to-amber-800 items-center justify-center p-12">
        <div className="text-center text-white max-w-md">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center mb-6">
            <Users className="w-9 h-9 text-white" />
          </div>
          <h2 className="text-3xl font-serif font-bold mb-4">Supervisor Access</h2>
          <p className="text-lg text-rose-100">
            Review your student&apos;s research proposal and provide feedback through inline comments.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-rose-700 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-rose-700 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-serif font-bold text-slate-900">StatsXY Supervisor</span>
          </div>

          <h1 className="text-3xl font-serif font-bold text-slate-900 mb-2">Supervisor Login</h1>
          <p className="text-slate-600 mb-8">
            Sign in with the credentials provided by the administrator to review your student&apos;s proposal.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="collab-login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                placeholder="supervisor@institution.edu"
                data-testid="collab-login-email"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                placeholder="••••••••"
                data-testid="collab-login-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full mt-6 bg-rose-700 hover:bg-rose-800 text-white"
              disabled={loading}
              data-testid="collab-login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In as Supervisor'}
            </Button>
          </form>

          <div className="mt-8 p-4 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-900">
            <strong>Note for supervisors:</strong> Your credentials are issued by the StatsXY administrator at the university&apos;s request. If you have not received your login, please contact the administrator.
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            Are you a student?{' '}
            <Link to="/login" className="text-indigo-700 hover:underline font-medium">
              Student login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CollaboratorLogin;
